import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';
import { LogEntry } from '../models/log-entry.model';

@Injectable({ providedIn: 'root' })
export class IndexedDbLogService {
  private dbPromise!: Promise<IDBPDatabase>;

  // Configuración de limpieza automática
  private readonly MAX_LOGS = 10000; // Máximo de logs permitidos
  private readonly MAX_AGE_DAYS = 30; // Eliminar logs más antiguos de 30 días
  private readonly CLEANUP_CHECK_INTERVAL = 100; // Verificar limpieza cada 100 logs

  constructor() {
    this.dbPromise = this.initDB();
  }

  private async initDB(): Promise<IDBPDatabase> {
    return openDB('AppLogsDB', 2, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // Si no existe el object store, crearlo
        if (!db.objectStoreNames.contains('logs')) {
          const store = db.createObjectStore('logs', { keyPath: 'id', autoIncrement: true });
          // Crear índice para búsqueda por timestamp (opcional, para futuras optimizaciones)
          try {
            if (!store.indexNames.contains('timestamp')) {
              store.createIndex('timestamp', 'timestamp');
            }
          } catch (e) {
            // Error al crear el índice, continuar sin él
            // El código funciona sin el índice usando getAll() y filtrando en memoria
          }
        } else {
          // Si el object store ya existe, intentar crear el índice si no existe
          const store = transaction.objectStore('logs');
          try {
            if (!store.indexNames.contains('timestamp')) {
              store.createIndex('timestamp', 'timestamp');
            }
          } catch (e) {
            // Error al crear el índice, continuar sin él
            // El código funciona sin el índice usando getAll() y filtrando en memoria
          }
        }
      },
    });
  }

  async addLog(entry: LogEntry): Promise<void> {
    const db = await this.dbPromise;

    // Verificar y limpiar antes de agregar nuevo log
    await this.cleanupIfNeeded(db);

    await db.add('logs', entry);
  }

  /**
   * Estrategia de limpieza automática para evitar sobrecarga de la base de datos
   * - Elimina logs más antiguos de MAX_AGE_DAYS días
   * - Si aún excede MAX_LOGS, elimina los más antiguos hasta llegar al límite
   */
  private async cleanupIfNeeded(db: IDBPDatabase): Promise<void> {
    try {
      const transaction = db.transaction('logs', 'readwrite');
      const store = transaction.objectStore('logs');

      // Obtener todos los logs ordenados por timestamp
      const allLogs = await store.getAll();

      if (allLogs.length === 0) return;

      const now = new Date();
      const maxAgeDate = new Date(now.getTime() - (this.MAX_AGE_DAYS * 24 * 60 * 60 * 1000));
      const maxAgeTimestamp = maxAgeDate.toISOString();

      let logsToDelete: number[] = [];

      // 1. Eliminar logs más antiguos de MAX_AGE_DAYS días
      const oldLogs = allLogs.filter(log => log.timestamp < maxAgeTimestamp);
      logsToDelete.push(...oldLogs.map(log => log.id!).filter((id): id is number => id !== undefined));

      // 2. Si aún excede MAX_LOGS después de eliminar logs antiguos, eliminar los más antiguos
      const remainingCount = allLogs.length - logsToDelete.length;
      if (remainingCount > this.MAX_LOGS) {
        const logsToKeep = this.MAX_LOGS;
        const logsToRemove = remainingCount - logsToKeep;

        // Ordenar logs restantes por timestamp (más antiguos primero)
        const sortedLogs = allLogs
          .filter(log => !logsToDelete.includes(log.id!))
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        // Agregar los más antiguos a la lista de eliminación
        const additionalLogsToDelete = sortedLogs
          .slice(0, logsToRemove)
          .map(log => log.id!)
          .filter((id): id is number => id !== undefined);

        logsToDelete.push(...additionalLogsToDelete);
      }

      // Eliminar logs en lote
      if (logsToDelete.length > 0) {
        await Promise.all(logsToDelete.map(id => store.delete(id)));
        console.info(`🧹 Limpieza automática: ${logsToDelete.length} logs eliminados`);
      }
    } catch (error) {
      console.error('Error durante la limpieza automática de logs:', error);
    }
  }

  async getAllLogs(): Promise<LogEntry[]> {
    const db = await this.dbPromise;
    return db.getAll('logs');
  }

  async getLogsByDateRange(startDate: Date, endDate: Date): Promise<LogEntry[]> {
    const db = await this.dbPromise;
    const transaction = db.transaction('logs', 'readonly');
    const store = transaction.objectStore('logs');

    const startTimestamp = startDate.toISOString();
    const endTimestamp = endDate.toISOString();

    const allLogs = await store.getAll();
    return allLogs.filter(log =>
      log.timestamp >= startTimestamp && log.timestamp <= endTimestamp
    );
  }

  async getLogCount(): Promise<number> {
    const db = await this.dbPromise;
    return db.count('logs');
  }

  async clearLogs(): Promise<void> {
    const db = await this.dbPromise;
    await db.clear('logs');
  }

  /**
   * Elimina logs más antiguos que la fecha especificada
   */
  async deleteLogsOlderThan(date: Date): Promise<number> {
    const db = await this.dbPromise;
    const transaction = db.transaction('logs', 'readwrite');
    const store = transaction.objectStore('logs');

    const cutoffTimestamp = date.toISOString();
    const allLogs = await store.getAll();
    const oldLogs = allLogs.filter(log => log.timestamp < cutoffTimestamp);
    const deletedCount = oldLogs.length;

    await Promise.all(oldLogs.map(log => store.delete(log.id!)));

    return deletedCount;
  }
}
